import { FC, ReactNode } from 'react';
import classNames from 'classnames';
import { Container, ContainerProps } from '@components/layout';
import { ThemeWrapperProps } from './ThemeWrapper';

export interface ComponentThemeWrapperProps extends ContainerProps {
  children: ReactNode;
  theme?: ThemeWrapperProps['theme'];
  useContainer?: boolean;
}

/**
 * Lightweight Component Theme Wrapper
 * Applies theme classes scoped to a single component subtree only. Does not
 * import any styles — assumes a parent `ThemeWrapper` has loaded the theme CSS/SCSS.
 * @param children - Any ReactNode component.
 * @param theme - The theme to apply. Default = 'dark'.
 * @param useContainer - Render children inside a Container component (true) or a plain div (false). Default = true.
 * @param className - Additional classes to apply to the container element.
 * @param styles - Inline styles forwarded to the container element.
 */
export const ComponentThemeWrapper: FC<ComponentThemeWrapperProps> = ({
  children,
  theme = 'dark',
  className,
  useContainer = true,
  styles,
  ...rest
}) => {
  const ContainerComponent = useContainer ? Container : 'div';
  const styleObj = useContainer ? { styles } : { style: styles };

  return (
    <ContainerComponent
      className={classNames(
        'vds-theme-wrapper',
        `vds-theme-wrapper--round`,
        `vds-${theme}-theme`,
        `vds-${theme}-theme__container`,
        className,
      )}
      {...styleObj}
      {...rest}>
      {children}
    </ContainerComponent>
  );
};
